/* eslint-disable no-param-reassign */
import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { v4 as uuidv4 } from 'uuid';
import { audioPlayerSelectors, audioPlayerActions } from 'ducks/audioPlayer';
import { logError } from 'utils/logger';
import { rewindAtValue } from './rewindAtValue';

export const useAudioPlayer = ({
	audioPlayerRef,
	progressBarRef,
	volumeBarRef,
	trackSrc
}) => {
	const [playerId, setPlayerId] = useState('');
	const { src: currentAudioSrc, playerId: currectPlayerId } = useSelector(
		audioPlayerSelectors.selectCurrentAudio
	);

	const dispatch = useDispatch();

	const animationRef = useRef(null);

	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);

	useEffect(() => {
		setPlayerId(uuidv4());
		const audioElement = new Audio(trackSrc);

		const handleLoadAudio = () => {
			setDuration(audioElement.duration);
		};

		audioElement?.addEventListener('loadedmetadata', handleLoadAudio);
		return () => {
			audioElement?.removeEventListener('loadedmetadata', handleLoadAudio);
		};
	}, []);

	useEffect(() => {
		if (
			currentAudioSrc !== audioPlayerRef.current.src ||
			playerId !== currectPlayerId
		) {
			setIsPlaying(false);
			audioPlayerRef.current.pause();
		}
	}, [currentAudioSrc, currectPlayerId]);

	useEffect(() => {
		if (audioPlayerRef?.current) {
			setDuration(audioPlayerRef?.current?.duration);
		}
	}, [audioPlayerRef?.current]);

	const getAndSetInitialFileDuration = () => {
		const fileDuration = audioPlayerRef?.current?.duration;
		if (fileDuration) {
			const seconds = Math.floor(fileDuration);
			setDuration(seconds);
			if (progressBarRef.current && seconds) {
				progressBarRef.current.max = seconds;
			}
		}
	};

	useEffect(() => {
		getAndSetInitialFileDuration();
		if (progressBarRef.current?.value) {
			progressBarRef.current?.style?.setProperty(
				'--seek-before-width',
				`${(Number(progressBarRef.current?.value) / duration) * 100}%`
			);
		}
	}, [currentTime]);

	const whilePlaying = () => {
		if (progressBarRef?.current?.value) {
			progressBarRef.current.value = audioPlayerRef.current?.currentTime;
		}
		setCurrentTime(audioPlayerRef.current?.currentTime);
		animationRef.current = requestAnimationFrame(whilePlaying);
	};

	const runPlaying = () => {
		const playPromise = audioPlayerRef.current.play();

		if (playPromise) {
			playPromise
				.then(() => {
					animationRef.current = requestAnimationFrame(whilePlaying);
					setIsPlaying(true);

					dispatch(
						audioPlayerActions.saveCurrentAudio({
							src: audioPlayerRef.current?.currentSrc,
							playerId
						})
					);
				})
				.catch((error) => {
					logError(error);
				});
		}
	};

	const calculateTime = (secs) => {
		const minutes = Math.floor(secs / 60);
		const returnedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
		const seconds = Math.floor(secs % 60);
		const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
		return `${returnedMinutes}:${returnedSeconds}`;
	};

	const changePlayerCurrentTime = () => {
		if (progressBarRef.current?.value) {
			progressBarRef.current?.style?.setProperty(
				'--seek-before-width',
				`${(Number(progressBarRef.current?.value) / duration) * 100}%`
			);
		}
		setCurrentTime(progressBarRef.current?.value);
	};

	const changeControlVolume = () => {
		volumeBarRef.current.style.setProperty(
			'--seek-before-width',
			`${volumeBarRef.current?.value}%`
		);
	};

	const playPauseToggle = () => {
		if (!isPlaying) {
			runPlaying();
		} else {
			audioPlayerRef.current.pause();
			cancelAnimationFrame(animationRef.current);
			setIsPlaying(false);
		}
	};

	const changeVolume = (event) => {
		if (event?.type) {
			audioPlayerRef.current.volume = event.target.value / 100;
			changeControlVolume();
		} else {
			audioPlayerRef.current.volume = event / 100;
		}
	};

	const dropRange = () => {
		setCurrentTime(0);
		if (!isPlaying) {
			runPlaying();
		}
	};

	const changeRange = (event) => {
		if (event?.type) {
			progressBarRef.current.value = event?.target?.value;
			audioPlayerRef.current.currentTime = event?.target?.value;
		} else {
			progressBarRef.current.value = audioPlayerRef.current.currentTime;
		}

		changePlayerCurrentTime();
	};

	const rewind = rewindAtValue(audioPlayerRef, changeRange);

	return {
		audioPlayerRef,
		progressBarRef,
		currentTime,
		duration,
		isPlaying,
		calculateTime,
		playPauseToggle,
		changeRange,
		changeVolume,
		dropRange,
		rewind
	};
};
